// AddViewSupportContestComponent.jsx  (อัปเดต: เพิ่มคอลัมน์ "หน่วยงาน" + Export ครอบคลุมคอลัมน์ใหม่)
import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Swal from "sweetalert2";
import "react-datepicker/dist/react-datepicker.css";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import DatePicker, { registerLocale } from "react-datepicker";
import th from "date-fns/locale/th";
import { addYears } from "date-fns/esm/fp";
import { th as thBuddhist } from "date-fns/esm/locale";
import { format, parseISO } from "date-fns";

// ====== หน่วยงาน ======
import Select from "react-select";
import { GetVMdDept } from "../../share/api/MasterDataApi";

// ====== Export libs ======
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ====== Locale: พ.ศ. ======
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

// ---------------------- Helpers ----------------------
const thaiMonths = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function fmtMoney(n) {
  const num = Number(String(n ?? 0).toString().replace(/,/g, "")) || 0;
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function toThaiDateLabel(iso) {
  if (!iso) return "-";
  if (typeof iso === "string" && /\s[ก-๙]/.test(iso)) return iso; // already BE label
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  if (!(d instanceof Date) || isNaN(d)) return "-";
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
const N = (v) => Number(String(v ?? 0).toString().replace(/,/g, "")) || 0;

// ====== PDF Thai font helpers ======
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(["addFonts", function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF();
  thaiFontLoaded = true;
}

// ===============================================================
//                       MAIN COMPONENT
// ===============================================================
export function OperationReport49Component() {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);

  // หน่วยงาน
  const [deptsData, setDeptsData] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");

  // โหลดหน่วยงานครั้งแรก
  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);

  // sync string <-> Date
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // helper: ดึงชื่อหน่วยงานจากหลาย key ที่เป็นไปได้
  const getApproveDept = (r) => r.deptName

  // รวมยอด footer
  const summary = useMemo(() => {
    const totalTarget = items.reduce((s, r) => s + N(r.targetPerson), 0);
    const totalActual = items.reduce((s, r) => s + N(r.targetActual), 0);
    const totalAlloc = items.reduce((s, r) => s + N(r.budgetAlloc), 0);
    const totalUsed = items.reduce((s, r) => s + N(r.budgetActual), 0);
    return { totalTarget, totalActual, totalAlloc, totalUsed, totalRemain: totalAlloc - totalUsed };
  }, [items]);

  // โหลดข้อมูล
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation49", {
        method: "POST",
        headers: { token: user.token },
        body: fd,
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];
      setItems(raw);
    } catch (e) {
      console.error(e);
      Swal.fire("โหลดข้อมูลเดือนไม่สำเร็จ", e?.message || "", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // ====== dept label for titles/files ======
  const deptLabel = useMemo(
    () => ([{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]
      .find(d => String(d.value) === String(deptIdFilter))?.label || "ทุกหน่วยงาน"),
    [deptsData, deptIdFilter]
  );

  // ----- Export Excel -----
  const buildAoaAndMerges = (rows) => {
    // เพิ่มคอลัมน์ "หน่วยงาน" (rowSpan=2) อยู่ก่อนกลุ่มจำนวนคน
    const aoa = [
      [
        "ลำดับ",
        "รายละเอียดค่าใช้จ่ายในการแข่งขันฝีมือแรงงาน",
        "ระดับ",
        "สาขา",
        "หน่วยงาน",
        "จำนวนคนผู้เข้าแข่งขัน", "",          // 2 คอลัมน์
        "จำนวนเงินค่าใช้จ่าย", "",            // 2 คอลัมน์
        "คงเหลือ (บาท)",
        "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
      ],
      [
        "", "", "", "", "",
        "กลุ่มเป้าหมาย (คน)",
        "ผู้เข้าร่วมงานแข่งขันจริง (คน)",
        "งบประมาณที่ได้รับจัดสรร (บาท)",
        "งบประมาณที่ใช้จริง (บาท)",
        "",
        "",
      ],
    ];
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // ลำดับ
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // รายละเอียด
      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // ระดับ
      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // สาขา
      { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }, // หน่วยงาน
      { s: { r: 0, c: 5 }, e: { r: 0, c: 6 } }, // กลุ่มคน (2 คอลัมน์)
      { s: { r: 0, c: 7 }, e: { r: 0, c: 8 } }, // เงิน (2 คอลัมน์)
      { s: { r: 0, c: 9 }, e: { r: 1, c: 9 } }, // คงเหลือ
      { s: { r: 0, c: 10 }, e: { r: 1, c: 10 } }, // วันที่จ่าย
    ];
    rows.forEach((r, idx) => {
      const remain = N(r.budgetAlloc) - N(r.budgetActual);
      aoa.push([
        idx + 1,
        r.detailContest || "-",
        r.detailLevel || "-",
        r.detailBranch || "-",
        getApproveDept(r),
        N(r.targetPerson),
        N(r.targetActual),
        N(r.budgetAlloc),
        N(r.budgetActual),
        N(remain),
        r.paymentDate ? r.paymentDate : "-",
      ]);
    });
    return { aoa, merges };
  };

  const handleExportExcel = () => {
    if (!items.length) return Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
    const { aoa, merges } = buildAoaAndMerges(items);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"] = [
      { wch: 8 },   // ลำดับ
      { wch: 38 },  // รายละเอียด
      { wch: 12 },  // ระดับ
      { wch: 16 },  // สาขา
      { wch: 26 },  // หน่วยงาน
      { wch: 16 },  // กลุ่มเป้าหมาย
      { wch: 20 },  // ผู้เข้าร่วมจริง
      { wch: 22 },  // ได้รับจัดสรร
      { wch: 18 },  // ใช้จริง
      { wch: 14 },  // คงเหลือ
      { wch: 28 },  // วันที่จ่าย
    ];

    // แถวรวม (ตำแหน่งคอลัมน์เลื่อนไป 1 ช่องเพราะมีคอลัมน์ "หน่วยงาน")
    const r = aoa.length;
    ws[XLSX.utils.encode_cell({ r, c: 0 })] = { t: "s", v: "รวม" };
    ws[XLSX.utils.encode_cell({ r, c: 5 })] = { t: "n", v: summary.totalTarget };
    ws[XLSX.utils.encode_cell({ r, c: 6 })] = { t: "n", v: summary.totalActual };
    ws[XLSX.utils.encode_cell({ r, c: 7 })] = { t: "n", v: summary.totalAlloc };
    ws[XLSX.utils.encode_cell({ r, c: 8 })] = { t: "n", v: summary.totalUsed };
    ws[XLSX.utils.encode_cell({ r, c: 9 })] = { t: "n", v: summary.totalRemain };
    // merge label "รวม" ครอบช่วงคอลัมน์ก่อนถึงกลุ่มตัวเลข (ลำดับ..หน่วยงาน = 0..4)
    ws["!merges"].push({ s: { r, c: 0 }, e: { r, c: 4 } });

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-9");
    const fileName = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-9.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ----- Export PDF -----
  const handleExportPDF = async () => {
    if (!items.length) return Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
    await ensureThaiFontLoaded();

    const titleMonth = formatMonthYearBE(reportMonthDate) || "ทุกเดือน";
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFont("Sarabun", "bold"); doc.setFontSize(16);
    doc.text(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-9`, 40, 40);

    const head = [
      [
        { content: "ลำดับ", rowSpan: 2 },
        { content: "รายละเอียดค่าใช้จ่ายในการแข่งขันฝีมือแรงงาน", rowSpan: 2 },
        { content: "ระดับ", rowSpan: 2 },
        { content: "สาขา", rowSpan: 2 },
        { content: "หน่วยงาน", rowSpan: 2 },
        { content: "จำนวนคนผู้เข้าแข่งขัน", colSpan: 2 },
        { content: "จำนวนเงินค่าใช้จ่าย", colSpan: 2 },
        { content: "คงเหลือ (บาท)", rowSpan: 2 },
        { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 2 },
      ],
      [
        { content: "กลุ่มเป้าหมาย (คน)" },
        { content: "ผู้เข้าร่วมงานแข่งขันจริง (คน)" },
        { content: "งบประมาณที่ได้รับจัดสรร (บาท)" },
        { content: "งบประมาณที่ใช้จริง (บาท)" },
      ],
    ];

    const body = items.map((r, idx) => {
      const remain = N(r.budgetAlloc) - N(r.budgetActual);
      return [
        idx + 1,
        r.detailContest || "-",
        r.detailLevel || "-",
        r.detailBranch || "-",
        getApproveDept(r),
        fmtMoney(r.targetPerson),
        fmtMoney(r.targetActual),
        fmtMoney(r.budgetAlloc),
        fmtMoney(r.budgetActual),
        fmtMoney(remain),
        r.paymentDate ? r.paymentDate : "-",
      ];
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head, body, startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4, halign: "center", overflow: "linebreak" },
      headStyles: { fillColor: [239, 243, 255], textColor: [0, 0, 0], font: "Sarabun", fontStyle: "bold" },
      columnStyles: {
        1: { halign: "left" },   // รายละเอียด
        2: { halign: "center" }, // ระดับ
        3: { halign: "center" }, // สาขา
        4: { halign: "left" },   // หน่วยงาน
        5: { halign: "right" },  // เป้าหมาย
        6: { halign: "right" },  // จริง
        7: { halign: "right" },  // ได้รับจัดสรร
        8: { halign: "right" },  // ใช้จริง
        9: { halign: "right" },  // คงเหลือ
        10: { halign: "center" }, // วันที่จ่าย
      },
      didDrawPage: (data) => {
        const n = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal"); doc.setFontSize(9);
        doc.text(`หน้า ${data.pageNumber} / ${n}`, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

    const y = (doc.lastAutoTable?.finalY || 60) + 24;
    doc.setFont("Sarabun", "bold"); doc.setFontSize(12);
    doc.text(
      `รวม: เป้าหมาย ${fmtMoney(summary.totalTarget)} คน | เข้าร่วมจริง ${fmtMoney(summary.totalActual)} คน | ` +
      `ได้รับจัดสรร ${fmtMoney(summary.totalAlloc)} | ใช้จริง ${fmtMoney(summary.totalUsed)} | ` +
      `คงเหลือ ${fmtMoney(summary.totalRemain)} บาท`,
      40, y
    );

    const fileName = `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-9.pdf`;
    doc.save(fileName);
  };

  return (
    <Container className="py-3">
      {/* ====== Filters ====== */}
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>ชื่อหน่วยงาน</Label>
          <Select
            isSearchable
            styles={{ control: (b) => ({ ...b, borderRadius: "20px" }), singleValue: (b) => ({ ...b, color: "#0A66C2" }) }}
            options={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]}
            value={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])].find(o => String(o.value) === String(deptIdFilter)) || null}
            onChange={(opt) => setDeptIdFilter(opt?.value ?? "all")}
            placeholder={<div className="input-color text-center">กรุณาเลือกหน่วยงาน</div>}
            className="rounded-pill text-center input-color"
          />
        </Col>

        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else { setReportMonth(""); setReportMonthDate(null); }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 140 }} value={thaiMonths[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                    {thaiMonths.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {years.map((y) => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={<input className="form-control rounded-pill text-center input-color" readOnly value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />}
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button type="button" className="btn btn-outline-success" onClick={handleExportExcel} disabled={!items.length}>ส่งออก Excel</button>
          <button type="button" className="btn btn-outline-danger" onClick={handleExportPDF} disabled={!items.length}>ส่งออก PDF</button>
        </Col>
      </Row>

      {/* ====== Table ====== */}
      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan="2" className="text-center align-middle">ที่</th>
              <th rowSpan="2" className="text-center align-middle">รายละเอียดค่าใช้จ่ายในการแข่งขันฝีมือแรงงาน</th>
              <th rowSpan="2" className="text-center align-middle">ระดับ</th>
              <th rowSpan="2" className="text-center align-middle">สาขา</th>
              <th rowSpan="2" className="text-center align-middle">หน่วยงาน</th>
              <th colSpan="2" className="text-center align-middle">จำนวนคนผู้เข้าแข่งขัน</th>
              <th colSpan="2" className="text-center align-middle">จำนวนเงินค่าใช้จ่าย</th>
              <th rowSpan="2" className="text-center align-middle">คงเหลือ<br />(บาท)</th>
              <th rowSpan="2" className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th className="text-center align-middle">กลุ่มเป้าหมาย<br />(คน)</th>
              <th className="text-center align-middle">ผู้เข้าร่วมงานแข่งขันจริง<br />(คน)</th>
              <th className="text-center align-middle">งบประมาณที่ได้รับจัดสรร<br />(บาท)</th>
              <th className="text-center align-middle">งบประมาณที่ใช้จริง<br />(บาท)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={11} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {items.map((r, idx) => {
              const remain = N(r.budgetAlloc) - N(r.budgetActual);
              return (
                <tr key={r.id || idx}>
                  <td className="text-center">{idx + 1}</td>
                  <td>{r.detailContest || "-"}</td>
                  <td className="text-center">{r.detailLevel || "-"}</td>
                  <td className="text-center">{r.detailBranch || "-"}</td>
                  <td className="text-start">{getApproveDept(r)}</td>
                  <td className="text-center">{fmtMoney(r.targetPerson)}</td>
                  <td className="text-center">{fmtMoney(r.targetActual)}</td>
                  <td className="text-center">{fmtMoney(r.budgetAlloc)}</td>
                  <td className="text-center">{fmtMoney(r.budgetActual)}</td>
                  <td className="text-center">{fmtMoney(remain)}</td>
                  <td className="text-center">{toThaiDateLabel(r.paymentDate)}</td>
                </tr>
              );
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                {/* ก่อนถึงกลุ่มตัวเลขตอนนี้มี 5 คอลัมน์ (ลำดับ-รายละเอียด-ระดับ-สาขา-หน่วยงาน) */}
                <td colSpan={5} className="text-end fw-bold">รวม</td>
                <td className="text-center fw-bold">{fmtMoney(summary.totalTarget)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.totalActual)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.totalAlloc)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.totalUsed)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.totalRemain)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <span className="badge bg-secondary">กำลังโหลด...</span>
        </div>
      )}
    </Container>
  );
}
